// Quote Builder — supplier-retailer discount cache + resolver.
//
// Single source of truth for the per-(supplier, retailer) discount data
// fetched from /api/catalogue/discounts. Both products-panel and the
// door-picker import from this module so they hit the same cache.
//
// Match precedence in `resolveDiscPct`:
//   0. Drilling (code ends -DRILLING / CIDDABS / CIDDBID)        → 0, always:
//      drilling is priced per hole and never takes a range discount
//   0b. The retailer's discount SHEET for the supplier (migration 0094,
//      discount-rates.ts): discount code, then range name               → sheet row
//   1. Category + door range (both supplied, both match)         → range-specific row
//   2. Category supplied, door range NULL                        → category fallback row
//   3. Door range supplied, category not                         → any-category range match
//      (only used by callers that don't know the catalogue category — e.g.
//      the door-picker's "Add bespoke" flow)
//   4. null                                                      → caller defaults to 0
//
// What is NOT used: hardcoded per-supplier default discounts. The platform's
// rule (Steve, 2026-05-25) is: never show a discount that hasn't been
// explicitly set by the supplier in their portal. Callers that previously
// fell back to `supplierDef.defaultDiscPct` now fall back to 0.

import { api } from "@tkos/api-client";
import { getCachedUser } from "../../shell/auth";
import { isDrillingCode, matchSheetRate, type RateLookup, type SheetRate } from "./discount-rates";

export interface DiscountRow {
  categoryId: string;
  categoryName: string;
  doorRange: string | null;
  discountPercent: number;
}

// The retailer slug the QB acts on — resolved from the signed-in session
// (slug preferred; UUID also resolves server-side via slugForRetailerId).
// Returns "" when not signed in, which yields no discounts rather than another
// tenant's.
function currentRetailerSlug(): string {
  return getCachedUser()?.retailerSlug || getCachedUser()?.retailerId || "";
}

const DISCOUNTS_CACHE = new Map<string, DiscountRow[]>(); // key = supplier slug (lowercased)
const RATES_CACHE = new Map<string, SheetRate[]>();       // same key; the 0094 sheet, in force today
const DISCOUNTS_LOADING = new Set<string>();              // de-dupe in-flight loads

export async function loadDiscountsFor(supplierSlug: string): Promise<void> {
  const key = supplierSlug.toLowerCase();
  if (DISCOUNTS_CACHE.has(key) || DISCOUNTS_LOADING.has(key)) return;
  DISCOUNTS_LOADING.add(key);
  try {
    const data = await api<{ ok: boolean; discounts?: DiscountRow[]; rates?: SheetRate[] }>(
      `/catalogue/discounts?supplier=${encodeURIComponent(key)}&retailer=${encodeURIComponent(currentRetailerSlug())}`,
    );
    DISCOUNTS_CACHE.set(key, Array.isArray(data.discounts) ? data.discounts : []);
    RATES_CACHE.set(key, Array.isArray(data.rates) ? data.rates : []);
  } catch {
    DISCOUNTS_CACHE.set(key, []);
    RATES_CACHE.set(key, []);
  } finally {
    DISCOUNTS_LOADING.delete(key);
  }
}

/** What a caller knows beyond category + range: the catalogue row's discount
 *  code, the door colour, the line's own code (to recognise drilling), and
 *  extra names to try (e.g. ["Jigsaw"]). */
export interface DiscExtra extends Pick<RateLookup, "discountCode" | "doorColour" | "names"> {
  code?: string | null;
  /** Match the sheet on `names` alone, ignoring category + range. For a line
   *  whose sheet row is its BRAND, not its range: every Jigsaw line takes the
   *  JIGSAW row even when a Jigsaw range shares a name with a TKC one. */
  sheetNamesOnly?: boolean;
}

export function resolveDiscPct(
  supplierSlug: string,
  categoryName: string | null | undefined,
  doorRange: string | null | undefined,
  extra: DiscExtra = {},
): number | null {
  if (isDrillingCode(extra.code)) return 0;
  const sheet = RATES_CACHE.get(supplierSlug.toLowerCase());
  if (sheet && sheet.length) {
    const only = extra.sheetNamesOnly === true;
    const m = matchSheetRate(sheet, {
      discountCode: only ? null : (extra.discountCode ?? null),
      doorRange: only ? null : (doorRange ?? null),
      doorColour: only ? null : (extra.doorColour ?? null),
      categoryName: only ? null : (categoryName ?? null),
      names: extra.names ?? [],
    });
    if (m) return m.rate.discountPercent;
  }
  const rows = DISCOUNTS_CACHE.get(supplierSlug.toLowerCase());
  if (!rows || rows.length === 0) return null;

  const catLc = categoryName ? categoryName.toLowerCase() : null;
  const drLc = doorRange ? doorRange.toLowerCase() : null;

  // 1. Category + range specific
  if (catLc && drLc) {
    const hit = rows.find(
      (r) =>
        r.categoryName != null
        && r.categoryName.toLowerCase() === catLc
        && r.doorRange != null
        && r.doorRange.toLowerCase() === drLc,
    );
    if (hit) return hit.discountPercent;
  }

  // 2. Category-wide fallback (door_range NULL)
  if (catLc) {
    const hit = rows.find(
      (r) =>
        r.categoryName != null
        && r.categoryName.toLowerCase() === catLc
        && r.doorRange == null,
    );
    if (hit) return hit.discountPercent;
  }

  // 3. Range-only match (any category). Used by callers that don't know
  // the PG catalogue category — e.g. the door picker's bespoke-add path.
  if (drLc && !catLc) {
    const hit = rows.find(
      (r) => r.doorRange != null && r.doorRange.toLowerCase() === drLc,
    );
    if (hit) return hit.discountPercent;
  }

  return null;
}

/** For tests / debugging — clears the in-memory caches. Not used in app code. */
export function _resetDiscountCacheForTests(): void {
  DISCOUNTS_CACHE.clear();
  RATES_CACHE.clear();
  DISCOUNTS_LOADING.clear();
}
